import React, { Component, type ReactNode } from 'react'
import { TopNav } from '@/shell/TopNav'
import { ModuleSidebar } from '@/shell/ModuleSidebar'
import { Breadcrumbs } from '@/shell/Breadcrumbs'
import { ErrorPanel } from '@/components/feedback/ErrorPanel'
import { modules } from '@/modules/registry'
import { useLocation } from 'react-router-dom'
import { Outlet } from 'react-router-dom'

// Shell-level error boundary
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

function isDeployChunkError(error: Error | null): boolean {
  if (!error) return false
  const message = error.message.toLowerCase()
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('is not a valid javascript mime type') ||
    message.includes('imported module')
  )
}

class AppErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[AppShell] Shell-level error:', error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <ErrorPanel
            title="Platform error"
            message={this.state.error?.message ?? 'An unexpected error occurred.'}
            onRetry={() => {
              if (isDeployChunkError(this.state.error)) {
                window.location.reload()
                return
              }
              this.setState({ hasError: false, error: null })
            }}
          />
        </div>
      )
    }
    return this.props.children
  }
}

function ShellContent() {
  const location = useLocation()
  const parts = location.pathname.split('/').filter(Boolean)
  const isDashboard = parts[0] === 'dashboard'
  const activeModuleId = isDashboard ? parts[1] ?? '' : ''
  const isLanding = !isDashboard
  const activeMod = modules.find((m) => m.basePath === `/dashboard/${activeModuleId}`)
  const showSidebar = isDashboard && !!activeMod

  return (
    <>
      <TopNav />
      {showSidebar && <ModuleSidebar routes={activeMod!.sidebar} />}
      <main
        style={{
          marginTop: 48,
          marginLeft: showSidebar ? 200 : 0,
          minHeight: 'calc(100vh - 48px)',
          padding: isLanding ? 0 : 'var(--space-lg)',
        }}
        className="transition-all duration-300"
      >
        {isLanding ? (
          <Outlet />
        ) : (
          <div className="max-w-platform mx-auto">
            <Breadcrumbs />
            <Outlet />
          </div>
        )}
      </main>
    </>
  )
}

export function AppShell() {
  return (
    <AppErrorBoundary>
      <ShellContent />
    </AppErrorBoundary>
  )
}
